<script setup lang="ts">
/**
 * Draws `state/slips.ts`. Mounted once, in `App.vue`; draws nothing until a
 * slip is pinned.
 *
 * A slip is a game-kit toast: a dark steel capsule with the tone's painted
 * icon (bell for a notice, star for a reward, red cross for a refusal), the
 * text in kit lettering and a rim in the tone's colour. Tap it to take it down. It drops in
 * (`y -16, scale .96 → 1`, 0.24 s, `back.out(1.8)`) and leaves to the right
 * (`x +40`, fade, 0.18 s) — life-layer.md §3 P1. Reduced motion: it just
 * appears and goes.
 */
import { playCue } from '@/audio/sfx';
import { dismissSlip, slips, type SlipTone } from '@/state/slips';
import GameIcon from '../kit/GameIcon.vue';
import type { GameIconName } from '../kit/icons';
import { enter, leave } from './gsap';

const IN = { y: -16, scale: 0.96, opacity: 0, duration: 0.24, ease: 'back.out(1.8)' };
const OUT = { x: 40, opacity: 0, duration: 0.18, ease: 'power2.in' };
const TONE_ICON: Record<SlipTone, GameIconName> = { notice: 'bell', reward: 'star', error: 'close' };
</script>

<template>
  <TransitionGroup
    tag="div"
    class="host"
    role="status"
    aria-live="polite"
    :css="false"
    data-test="slip-host"
    @enter="(el, done) => { playCue('slip.pin'); enter(el, IN, done); }"
    @leave="(el, done) => leave(el, OUT, done)"
  >
    <button
      v-for="s in slips"
      :key="s.id"
      type="button"
      class="slip"
      :class="s.tone"
      :role="s.tone === 'error' ? 'alert' : undefined"
      :data-test="`slip-${s.tone}`"
      @click="dismissSlip(s.id)"
    >
      <GameIcon class="icon" :name="TONE_ICON[s.tone]" :size="40" />
      <span class="text k-label">{{ s.text }}</span>
      <span v-if="s.detail" class="detail">{{ s.detail }}</span>
    </button>
  </TransitionGroup>
</template>

<style scoped>
/* Top-right, under the notch, above the tarp (50). The extra offset keeps the
   stack clear of the harbour's purse tag in the same corner. */
.host {
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 6.5rem);
  right: calc(env(safe-area-inset-right, 0px) + 0.75rem);
  z-index: 60;
  display: grid;
  justify-items: end;
  gap: 0.5rem;
  width: min(20rem, calc(100vw - 1.5rem));
  pointer-events: none;
}

.slip {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  column-gap: 0.6rem;
  row-gap: 0.1rem;
  min-height: 2.75rem;
  max-width: 100%;
  padding: 0.45rem 1rem 0.45rem 0.45rem;
  border: 0;
  border-radius: 999px;
  text-align: left;
  background: linear-gradient(180deg, #2b3f55, #16222f 60%, #0e1721);
  cursor: pointer;
  pointer-events: auto;
  touch-action: manipulation;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.18),
    0 0 0 3px var(--edge),
    0 0 0 5px var(--kit-ink),
    0 8px 18px rgba(0, 0, 0, 0.5);
}

/* Rim colours: the kit's button paints (notice navy-blue, reward gold, refusal red). */
.notice {
  --edge: #4d8fd1;
}

.reward {
  --edge: var(--kit-gold);
}

.error {
  --edge: #e0453a;
}

.icon {
  grid-row: span 2;
}

.text {
  font-size: 0.95rem;
  overflow-wrap: anywhere;
}

.detail {
  grid-column: 2;
  font-family: var(--font-game-body);
  font-weight: 500;
  font-size: 0.8rem;
  color: #b8c7d6;
}

.slip:focus-visible {
  outline: 2px solid var(--kit-gold);
  outline-offset: 4px;
}
</style>
